'use client';

import React, { useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, MapPin, User } from 'lucide-react';

import AppointmentCard from '@/components/work-items/AppointmentCard';
import WorkItemHoverCard from '@/components/work-items/WorkItemHoverCard';
import {
  getAppointmentDisplayNumber,
  getAppointmentGroupLeaderDisplayName,
  isAppointmentGroupLeader,
  isAppointmentLikeItem,
} from '@/lib/appointmentDisplay';
import { buildAppointmentCardPresentation } from '@/lib/appointmentCardPresentation';
import { buildWorkItemTooltipLines } from '@/lib/workItemTooltip';
import { authService } from '@/lib/authService';
import { canSeeAppointmentRescheduleState } from '@/lib/work-items/appointmentRescheduleState';

import type { WorkItem } from '../UniversalWorkItems';
import type { WorkItemTemplate } from '../WorkItemModal';
import type { KanbanStylePreset } from './KanbanStylePresets';

interface ScheduleSidebarItemCardProps {
  item: WorkItem;
  template: WorkItemTemplate | null;
  isDark: boolean;
  onClick: () => void;
  isDragging?: boolean;
  onDragStart?: (e: React.DragEvent, item: WorkItem) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  presetConfig?: KanbanStylePreset;
  enableHoverCard?: boolean;
  groupLeaderNamesByGroupKey?: Map<string, string>;
  dragMode?: 'html5' | 'sortable';
}

function getAppointmentAssigneeName(item: WorkItem): string | null {
  const primaryAssignee = item.assignees?.[0];
  if (!primaryAssignee) {
    return null;
  }

  const displayName = [primaryAssignee.first_name, primaryAssignee.last_name]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .join(' ')
    .trim();

  return displayName || primaryAssignee.email || null;
}

export default function ScheduleSidebarItemCard({
  item,
  template,
  isDark,
  onClick,
  isDragging = false,
  onDragStart,
  onDragEnd,
  presetConfig,
  enableHoverCard = true,
  groupLeaderNamesByGroupKey,
  dragMode = 'html5',
}: ScheduleSidebarItemCardProps) {
  const currentUser = useMemo(() => authService.getUser(), []);
  const canSeeRescheduleStatus = useMemo(
    () => canSeeAppointmentRescheduleState(currentUser),
    [currentUser],
  );
  const isSortableMode = dragMode === 'sortable';
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: item.id,
    disabled: !isSortableMode,
  });

  const resolvedIsDragging = isDragging || isSortableDragging;
  const interactiveStyle: React.CSSProperties = {
    userSelect: 'none',
    WebkitUserSelect: 'none',
    ...(isSortableMode
      ? {
          transform: CSS.Transform.toString(transform),
          transition,
          touchAction: 'none',
        }
      : {}),
  };
  const dragInteractionProps = isSortableMode
    ? {
        ref: setNodeRef,
        ...attributes,
        ...listeners,
      }
    : {
        draggable: true,
        onDragStart: (e: React.DragEvent) => {
          e.stopPropagation();
          onDragStart?.(e, item);
        },
        onDragEnd: (e: React.DragEvent) => {
          e.stopPropagation();
          onDragEnd?.(e);
        },
      };

  const priorityBorderColor = {
    urgent: 'border-l-red-600',
    critical: 'border-l-red-700',
    high: 'border-l-orange-500',
    medium: 'border-l-yellow-500',
    low: 'border-l-green-500',
  }[item.priority || 'medium'] || 'border-l-gray-400';
  const wrapperClassName = `
    group ${presetConfig?.card?.background || 'bg-white dark:bg-gray-800'}
    ${presetConfig?.card?.border || 'border border-gray-200 dark:border-gray-700'}
    ${presetConfig?.card?.borderRadius || 'rounded'}
    ${presetConfig?.card?.shadow || ''}
    ${priorityBorderColor}
    p-2 ${isSortableMode ? 'cursor-grab active:cursor-grabbing' : 'cursor-move'} transition-all
    ${presetConfig?.card?.backgroundHover || 'hover:bg-gray-50 dark:hover:bg-gray-750'}
    ${presetConfig?.card?.shadowHover || 'hover:shadow-md'}
    select-none
    ${resolvedIsDragging ? 'opacity-50' : ''}
  `;

  if (isAppointmentLikeItem(item)) {
    const itemNumber = getAppointmentDisplayNumber(item) || null;
    const assigneeName = getAppointmentAssigneeName(item);
    const isGroupLeader = isAppointmentGroupLeader(item);
    const groupLeaderName = getAppointmentGroupLeaderDisplayName(item, assigneeName);
    const appointmentVisuals = buildAppointmentCardPresentation(item, isDark, {
      appointmentPrivilegedViewer: canSeeRescheduleStatus,
    });

    return (
      <div
        className={`overflow-visible transition-all select-none ${isSortableMode ? 'cursor-grab active:cursor-grabbing' : 'cursor-move'} ${resolvedIsDragging ? 'opacity-50' : ''}`}
        style={interactiveStyle}
        onClick={onClick}
        {...dragInteractionProps}
      >
        <AppointmentCard
          appointment={{
            id: item.id,
            title: item.title || item.item_number || 'Untitled appointment',
            itemNumber,
            groupNumber: null,
            isGroupLeader,
            groupLeaderName,
            parentJobNumber: (item as any).parent_job_number || null,
            timingLabel: appointmentVisuals.timingLabel,
            timingTone: appointmentVisuals.timingTone,
            statusLabel: appointmentVisuals.statusLabel,
            statusTone: appointmentVisuals.statusTone,
            priorityLabel: appointmentVisuals.priorityLabel,
            priorityTone: appointmentVisuals.priorityTone,
            scheduledStart: item.scheduled_start,
            scheduledEnd: item.scheduled_end,
            assigneeName,
            contactName: item.contact_name || null,
            locationLabel: item.location_name || item.location_address || null,
            description: item.description || null,
            isGrouped: false,
            tooltipLines: buildWorkItemTooltipLines(item, { appointmentPrivilegedViewer: canSeeRescheduleStatus }),
          }}
          compact
          interactive={enableHoverCard}
          omitDefaultBorder
          className={`${appointmentVisuals.cardClassName} ${presetConfig?.card?.borderRadius || 'rounded'}`}
          contentClassName="p-2.5"
        />
      </div>
    );
  }

  const cardBody = (
    <>
      <div className="flex items-start gap-2">
        <GripVertical className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-1 min-w-0">
            {template?.icon ? (
              <span className="text-xs leading-none mt-0.5 flex-shrink-0">{template.icon}</span>
            ) : null}

            <span className={`text-xs font-semibold truncate min-w-0 flex-shrink max-w-[130px] ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
              {item.item_number}
            </span>
          </div>

          <div className={`text-xs font-medium line-clamp-2 leading-snug mt-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {item.title}
          </div>

          {item.contact_name ? (
            <div className={`text-[10px] truncate mt-1 flex items-center gap-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              <User className="w-3 h-3 text-gray-400 flex-shrink-0" />
              {item.contact_name}
            </div>
          ) : null}

          {(item.location_name || item.location_address) ? (
            <div className={`text-[10px] truncate mt-0.5 flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
              {item.location_name || item.location_address?.split(',')[0]}
            </div>
          ) : item.description ? (
            <div className={`text-[10px] line-clamp-2 leading-tight mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {item.description}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-1 mt-1.5">
            {item.status && !['open', 'scheduled', 'backlog'].includes(item.status) ? (
              <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${
                item.status === 'completed' || item.status === 'closed'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : item.status === 'in_progress' || item.status === 'active'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    : item.status === 'pending_closure' || item.status === 'review'
                      ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                      : item.status === 'cancelled' || item.status === 'blocked'
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}>
                {item.status.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );

  if (!enableHoverCard) {
    return (
      <div
        className={wrapperClassName}
        style={interactiveStyle}
        onClick={onClick}
        {...dragInteractionProps}
      >
        {cardBody}
      </div>
    );
  }

  return (
    <WorkItemHoverCard
      as="div"
      workItem={item}
      className={wrapperClassName}
      style={interactiveStyle}
      onClick={onClick}
      {...dragInteractionProps}
    >
      {cardBody}
    </WorkItemHoverCard>
  );
}